import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Alert, Snackbar } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useRulesData } from './hooks/useRulesData';
import { useFilters } from './hooks/useFilters';
import { useSorting } from './hooks/useSorting';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Chip,
  Stack,
  Tooltip,
  styled,
  CircularProgress,
  Divider,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  TablePagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  FolderOpen as FolderOpenIcon,
  Clear as ClearIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Sort as SortIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
  UnfoldMore
} from '@mui/icons-material';
import { Menu, ListItemIcon, ListItemText } from '@mui/material';

// Import TransactionList component
import TransactionList from '../Transactions/TransactionList';
import RuleFormDialog from './components/RuleFormDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import PageHeader from '../common/PageHeader';
import UncategorizedProgressCard from './components/UncategorizedProgressCard';
import AiRuleSuggestionsCard from './components/AiRuleSuggestionsCard';
import AiConsolidateDialog from './components/AiConsolidateDialog';
import TransactionReviewCard from './components/TransactionReviewCard';
import RulesTable from './components/RulesTable';
import RulesImportDialog from './components/RulesImportDialog';
import { exportRulesToCSV } from '../../utils/rulesExportUtils';

// Styled components
const StyledRuleCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  padding: theme.spacing(1, 3),
}));

const ConditionChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.8125rem',
  height: 28,
  backgroundColor: theme.palette.primary.lightest,
  color: theme.palette.primary.main,
  '& .MuiChip-label': { fontWeight: 500 }
}));

const PriorityIndicator = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  fontSize: '0.8125rem'
}));

// New styled components for condition groups
const ConditionGroupBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const OrChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  fontSize: '0.8125rem',
  marginBottom: theme.spacing(0.5),
}));


// --- Scrollable Filters Styled Components ---
const ScrollableFiltersContainer = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 20,
    zIndex: 2,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease',
  },
  '&::before': {
    left: 0,
    background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
  },
  '&::after': {
    right: 0,
    background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
  },
  '&.show-left-fade::before': {
    opacity: 1,
  },
  '&.show-right-fade::after': {
    opacity: 1,
  },
  '&:not(.show-left-fade)::before': {
    opacity: 0,
  },
  '&:not(.show-right-fade)::after': {
    opacity: 0,
  },
}));

const ScrollableFilters = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  paddingBottom: theme.spacing(0.5),
  paddingTop: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  margin: `0 ${theme.spacing(1)}`,
  scrollbarWidth: 'none',
  '-ms-overflow-style': 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '& > *': {
    flexShrink: 0,
  },
}));

const ScrollButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 3,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(4px)',
  border: `1px solid ${theme.palette.divider}`,
  width: 32,
  height: 32,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-50%) scale(1.05)',
  },
  '&.left': {
    left: 8,
  },
  '&.right': {
    right: 8,
  },
}));

const FilterToggleButton = styled(Button)(({ theme, selected }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: theme.typography.body2.fontSize,
  height: 36,
  padding: theme.spacing(0.75, 2),
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  border: `1px solid ${theme.palette.grey[300]}`,
  minWidth: 'auto',
  boxShadow: 'none',
  ...(selected ? {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      transform: 'translateY(-1px)',
    },
  } : {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.text.primary,
    borderColor: theme.palette.grey[300],
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      borderColor: theme.palette.grey[400],
      transform: 'translateY(-1px)',
    },
  }),
}));

// --- useScrollableFilters Hook ---
const useScrollableFilters = () => {
  const scrollRef = useRef(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const checkScrollButtons = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      checkScrollButtons();
      scrollElement.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        scrollElement.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [checkScrollButtons]);

  return {
    scrollRef,
    showLeftScroll,
    showRightScroll,
    scrollLeft,
    scrollRight,
    checkScrollButtons
  };
};

// --- ProgressSortIcon (custom sort icon button) ---
// Simple Progress Bar Sort Icon
const ProgressSortIcon = ({ 
  sortDirection = null, // null, 'asc', 'desc'
  onClick,
  size = 20
}) => {
  const barHeight = 3;
  const barSpacing = 2;
  
  const getBarWidths = () => {
    if (sortDirection === 'desc') return [size * 0.8, size * 0.6, size * 0.4];
    return [size * 0.4, size * 0.6, size * 0.8];
  };
  
  const barWidths = getBarWidths();

  return (
    <ActionButton
      onClick={onClick}
      variant="outlined"
      color="primary"
      size="small"
      sx={(theme) => ({
        bgcolor: 'transparent',
        borderColor: 'primary.main',
        color: 'primary.main',
        whiteSpace: 'nowrap',
        '&:hover': {
          bgcolor: theme.palette.primary.lightest,
          borderColor: 'primary.main',
        },
        height: '35px',
        width: '40px',
        minWidth: '40px',
        minHeight: '35px',
        padding: '8px',
        borderRadius: 1,
      })}
      aria-label="Sort"
      tabIndex={0}
      type="button"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${barSpacing}px`,
          width: size,
          height: size,
          justifyContent: 'center'
        }}
      >
        {barWidths.map((width, index) => (
          <div
            key={index}
            style={{
              width: `${width}px`,
              height: `${barHeight}px`,
              backgroundColor: 'currentColor',
              borderRadius: `${barHeight / 2}px`,
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
    </ActionButton>
  );
};

const RulesManager = () => {
  // Data, filter, and sorting hooks
  const {
    rules,
    groupedRules,
    categories,
    transactions,
    availableFields,
    isLoading,
    result,
    applyingRules,
    aiCategorizing,
    aiSuggestions,
    aiRuleSuggestions,
    aiMessage,
    aiConsolidating,
    consolidationProposals,
    uncategorizedCount,
    categorizeSuccess,
    categorizeMessage,
    handleRunAll,
    handleRunSelected,
    handleRunSingle,
    handleRunCategoryRules,
    handleCategorizeAll,
    handleAiCategorize,
    handleAcceptAiSuggestion,
    handleRejectAiSuggestion,
    handleCreateAiRule,
    handleDismissAiRule,
    handleDismissAiMessage,
    handleConsolidateRules,
    handleApplyConsolidation,
    handleCloseConsolidation,
    handleAddRule,
    handleUpdateRule,
    handleDeleteRule,
    handleBulkCategorize,
    getCategoryName,
    formatCondition,
    setResult
  } = useRulesData();
  const filters = useFilters();
  const sorting = useSorting();

  // Minimal UI state
  const [open, setOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  // key to force remount of RuleFormDialog so each open is a fresh instance
  const [dialogKey, setDialogKey] = useState(0);
  const [selected, setSelected] = useState([]);
  const [priorityInfoOpen, setPriorityInfoOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState(null);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  // Drop selections that point at rules which no longer exist (e.g. after
  // a consolidation merge deletes the originals)
  useEffect(() => {
    setSelected(prev => {
      const next = prev.filter(id => rules.some(r => r._id === id));
      return next.length === prev.length ? prev : next;
    });
  }, [rules]);

  // Handler for selecting rules
  const handleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = (ruleIds, checked) => {
    if (checked) {
      setSelected(prev => [...new Set([...prev, ...ruleIds])]);
    } else {
      setSelected(prev => prev.filter(id => !ruleIds.includes(id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    setPendingDeleteIds([...selected]);
    setDeleteConfirmOpen(true);
  };

  const requestDeleteRule = (id) => {
    setPendingDeleteIds([id]);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmOpen(false);
    const ids = [...pendingDeleteIds];
    setPendingDeleteIds([]);
    const count = ids.length;
    try {
      for (const id of ids) {
        await handleDeleteRule(id);
      }
      setSelected(prev => prev.filter(id => !ids.includes(id)));
      if (count > 1) {
        setImportMessage({ type: 'success', text: `${count} rules deleted` });
        setTimeout(() => setImportMessage(null), 3000);
      }
    } catch (error) {
      setImportMessage({ type: 'error', text: 'Failed to delete some rules' });
      setTimeout(() => setImportMessage(null), 3000);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  // Handler for creating a rule from a transaction
  const handleCreateRuleFromTransaction = (txn) => {
    const conditions = [
      { field: 'description', operator: 'contains', value: txn.description || '' }
    ];

    if (txn.meta) {
      if (txn.meta.reference) {
        conditions.push({
          field: 'meta.reference',
          operator: 'contains',
          value: txn.meta.reference
        });
      }
      if (txn.meta.particulars) {
        conditions.push({
          field: 'meta.particulars',
          operator: 'contains',
          value: txn.meta.particulars
        });
      }
    }

    if (txn.account) {
      conditions.push({ field: 'account', operator: 'contains', value: txn.account });
    }

    if (typeof txn.amount === 'number') {
      // store amount as string to preserve formatting; RuleFormDialog will accept it as condition value
      conditions.push({ field: 'amount', operator: 'equals', value: String(txn.amount) });
    }

    const prefilled = {
      conditionGroups: [{ conditions }],
      categoryId: txn.category || '',
      priority: 1
    };

    setEditRule(prefilled);
    // bump key so the dialog remounts and internal state is fresh
    setDialogKey(k => k + 1);
    setOpen(true);
  };

  // Date formatting helper
  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / (1000 * 60 * 60 * 24);
    if (diff < 1) return 'Today';
    if (diff < 2) return 'Yesterday';
    if (diff < 7) return `${Math.floor(diff)} days ago`;
    return date.toLocaleDateString();
  };

  // Download all rules as JSON
  const handleDownloadRules = () => {
    try {
      exportRulesToCSV(rules, categories);
      setImportMessage({ type: 'success', text: 'Rules downloaded successfully' });
      setTimeout(() => setImportMessage(null), 3000);
    } catch (error) {
      setImportMessage({ type: 'error', text: 'Failed to download rules' });
      setTimeout(() => setImportMessage(null), 3000);
    }
  };

  // Import rules from JSON
  const handleImportRules = async (importedRules) => {
    setImportLoading(true);
    try {
      // Add each rule
      const addPromises = importedRules.map(rule => {
        // Remove categoryName if it exists (it's for reference only)
        const { categoryName, ...ruleData } = rule;
        return handleAddRule(ruleData);
      });

      await Promise.all(addPromises);
      setImportMessage({ 
        type: 'success', 
        text: `Successfully imported ${importedRules.length} rule(s)` 
      });
      setTimeout(() => setImportMessage(null), 3000);
    } catch (error) {
      setImportMessage({ 
        type: 'error', 
        text: 'Failed to import rules: ' + (error.message || 'Unknown error') 
      });
      setTimeout(() => setImportMessage(null), 3000);
      throw error;
    } finally {
      setImportLoading(false);
    }
  };

  if (isLoading) return null;

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <PageHeader
        title="Rules"
        icon={<LightbulbOutlinedIcon fontSize="large" />}
      />
      {/* Overflow guard sits below the header so it can't clip the
          full-bleed negative margins */}
      <Box sx={{ overflow: 'hidden' }}>

      {/* AI feedback banner — auto-dismisses (errors stay until closed) */}
      {aiMessage && (
        <Alert
          severity={aiMessage.severity}
          onClose={handleDismissAiMessage}
          sx={{
            mb: { xs: 2, sm: 3 },
            borderRadius: 2,
            alignItems: 'center',
            fontSize: { xs: '0.85rem', sm: '0.9rem' }
          }}
        >
          {aiMessage.text}
        </Alert>
      )}

      {/* Main content */}
      <UncategorizedProgressCard
        uncategorizedCount={uncategorizedCount}
        totalTransactions={transactions.length}
        categorizeSuccess={categorizeSuccess}
        categorizeMessage={categorizeMessage}
        applyingRules={applyingRules}
        aiCategorizing={aiCategorizing}
        onCategorizeAll={handleCategorizeAll}
        onAiCategorize={handleAiCategorize}
        onCreateRule={() => { setEditRule(null); setDialogKey(k => k + 1); setOpen(true); }}
      />
      <AiRuleSuggestionsCard
        ruleSuggestions={aiRuleSuggestions}
        onCreateRule={handleCreateAiRule}
        onDismiss={handleDismissAiRule}
      />
      <TransactionReviewCard
        transactions={transactions}
        categories={categories}
        filters={filters}
        onCreateRuleFromTransaction={handleCreateRuleFromTransaction}
        onBulkCategorize={handleBulkCategorize}
        getCategoryName={getCategoryName}
        formatRelativeDate={formatRelativeDate}
        sorting={sorting}
        aiSuggestions={aiSuggestions}
        onAcceptAiSuggestion={handleAcceptAiSuggestion}
        onRejectAiSuggestion={handleRejectAiSuggestion}
        onAiCategorizeSelected={handleAiCategorize}
        aiCategorizing={aiCategorizing}
      />
      <AiConsolidateDialog
        proposals={consolidationProposals}
        onApply={handleApplyConsolidation}
        onClose={handleCloseConsolidation}
      />
      <RulesTable
        groupedRules={groupedRules}
        rules={rules}
        categories={categories}
        selected={selected}
        onSelect={handleSelect}
        applyingRules={applyingRules}
        rulesCategoryFilter={filters.rulesCategoryFilter}
        onRulesCategoryFilterChange={filters.setRulesCategoryFilter}
        onRunAll={handleRunAll}
        onRunSelected={() => handleRunSelected(selected)}
        onRunSingle={handleRunSingle}
        onCreateRule={() => { setEditRule(null); setDialogKey(k => k + 1); setOpen(true); }}
        onEditRule={(rule) => { setEditRule(rule); setDialogKey(k => k + 1); setOpen(true); }}
        onDeleteRule={requestDeleteRule}
        formatCondition={formatCondition}
        getCategoryName={getCategoryName}
        sorting={sorting}
        onDownloadRules={handleDownloadRules}
        onImportRules={() => setImportDialogOpen(true)}
        onConsolidate={() => handleConsolidateRules(selected)}
        aiConsolidating={aiConsolidating}
        onSelectAll={handleSelectAll}
        onDeleteSelected={handleDeleteSelected}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        title={pendingDeleteIds.length === 1 ? 'Delete Rule' : `Delete ${pendingDeleteIds.length} Rules`}
        message={pendingDeleteIds.length === 1
          ? 'Are you sure you want to delete this rule? This action cannot be undone.'
          : `Are you sure you want to delete ${pendingDeleteIds.length} rules? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />

      <RuleFormDialog
        // key forces remount so internal dialog state is reset each time it's opened
        key={dialogKey}
        open={open}
        onClose={() => setOpen(false)}
        editRule={editRule}
        categories={categories}
        availableFields={availableFields}
        onSubmit={async (payload, editRuleArg) => {
          // Consider this an edit only if the editRuleArg has an identifier
          const id = editRuleArg && (editRuleArg._id || editRuleArg.id);
          if (id) {
            return await handleUpdateRule(id, payload);
          }
          return await handleAddRule(payload);
        }}
        formatCondition={formatCondition}
        getCategoryName={getCategoryName}
      />
      
      {/* Import Dialog */}
      <RulesImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        categories={categories}
        onImport={handleImportRules}
        isLoading={importLoading}
      />
      
      {/* Feedback Snackbar */}
      <Snackbar
        open={importMessage !== null}
        autoHideDuration={3000}
        onClose={() => setImportMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setImportMessage(null)}
          severity={importMessage?.type || 'success'}
          sx={{ width: '100%' }}
        >
          {importMessage?.text}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default RulesManager;